import { CustomerContact, CustomerContactPurpose } from '../types'

type ContactRoles = Pick<
  CustomerContact,
  'purposes' | 'notificationSubscriptions'
>

export const isPrimaryBillingContact = (
  contact: Partial<ContactRoles>
): boolean => Boolean(contact.purposes?.includes('BILLING'))

export const isShippingContact = (contact: Partial<ContactRoles>): boolean =>
  Boolean(contact.purposes?.includes('SHIPPING'))

export const receivesBillingNotifications = (
  contact: Partial<ContactRoles>
): boolean => Boolean(contact.notificationSubscriptions?.includes('BILLING'))

export const setBillingNotifications = <Contact extends ContactRoles>(
  contact: Contact,
  isSubscribed: boolean
): Contact => ({
  ...contact,
  notificationSubscriptions: isSubscribed
    ? [
        ...contact.notificationSubscriptions.filter(
          subscription => subscription !== 'BILLING'
        ),
        'BILLING'
      ]
    : contact.notificationSubscriptions.filter(
        subscription => subscription !== 'BILLING'
      )
})

/**
 * Gives one contact a purpose and takes it off everyone else. A customer only
 * has one primary billing contact and one shipping contact, and the backend
 * won't demote the previous holder for us. Both roles always receive
 * notifications, so the new holder is subscribed too.
 */
export const assignContactPurpose = <
  Contact extends ContactRoles & { id: string }
>(
  contacts: Contact[],
  contactId: string,
  purpose: CustomerContactPurpose
): Contact[] =>
  contacts.map(contact =>
    contact.id === contactId
      ? setBillingNotifications(withPurpose(contact, purpose), true)
      : withoutPurpose(contact, purpose)
  )

export const unassignContactPurpose = <
  Contact extends ContactRoles & { id: string }
>(
  contacts: Contact[],
  contactId: string,
  purpose: CustomerContactPurpose
): Contact[] =>
  contacts.map(contact =>
    contact.id === contactId ? withoutPurpose(contact, purpose) : contact
  )

export const withoutPurpose = <Contact extends ContactRoles>(
  contact: Contact,
  purpose: CustomerContactPurpose
): Contact => ({
  ...contact,
  purposes: contact.purposes.filter(existing => existing !== purpose)
})

const withPurpose = <Contact extends ContactRoles>(
  contact: Contact,
  purpose: CustomerContactPurpose
): Contact => ({
  ...contact,
  purposes: [...withoutPurpose(contact, purpose).purposes, purpose]
})
